import React, { useState, useEffect } from 'react';
import { X, AlertCircle, Trophy, Bug, ChevronUp, ChevronDown, Lightbulb, Share2 } from 'lucide-react';
import './Game.css';
import { diseases } from './diseases';

// Function to get today's date as a string (YYYY-MM-DD format)
const getTodayString = () => {
  const today = new Date();
  return today.toISOString().split('T')[0];
};

// Function to get the daily disease based on date
const getDailyDisease = (dateString) => {
  // Parse the date
  const currentDate = new Date(dateString);
  
  // Get the day of the year (1-365/366)
  const startOfYear = new Date(currentDate.getFullYear(), 0, 1);
  const dayOfYear = Math.floor((currentDate - startOfYear) / (24 * 60 * 60 * 1000)) + 1;
  
  // Calculate the index by taking modulo of diseases array length
  // This ensures we cycle through the list when we reach the end
  const diseaseIndex = (dayOfYear - 1) % diseases.length;
  
  return diseases[diseaseIndex];
};
// Function to save today's game state
const saveTodayGameState = (guesses, gameWon, showHint, showSecondHint) => {
  if (typeof window === 'undefined' || !window.localStorage) return;
  const today = getTodayString();
  const gameState = {
    date: today,
    guesses,
    gameWon,
    showHint,
    showSecondHint
  };
  localStorage.setItem('diseaseGameTodayState', JSON.stringify(gameState));

  // Only mark as "played" if the game is actually complete
  if (gameWon) {
    localStorage.setItem('diseaseGameLastPlayed', today);
  }
};

// Function to load today's game state
const loadTodayGameState = () => {
  if (typeof window === 'undefined' || !window.localStorage) return null;
  const savedState = localStorage.getItem('diseaseGameTodayState');
  if (!savedState) return null;

  const gameState = JSON.parse(savedState);
  if (gameState.date !== getTodayString()) return null;

  return gameState;
};

export default function Game() {
  const [targetDisease, setTargetDisease] = useState(null);
  const [guesses, setGuesses] = useState([]);
  const [currentGuess, setCurrentGuess] = useState('');
  const [suggestions, setSuggestions] = useState([]);
  const [gameWon, setGameWon] = useState(false);
  const [streak, setStreak] = useState(0);
  const [hasCompletedToday, setHasCompletedToday] = useState(false);
  const [showHint, setShowHint] = useState(false);
  const [showSecondHint, setShowSecondHint] = useState(false);
  const [shareMessage, setShareMessage] = useState('');

  useEffect(() => {
    initializeGame();
    // Load streak from localStorage if available in your environment
    if (typeof window !== 'undefined' && window.localStorage) {
      const savedStreak = localStorage.getItem('diseaseGameStreak');
      if (savedStreak) {
        setStreak(parseInt(savedStreak));
      }
    }
  }, []);

  const initializeGame = () => {
    const today = getTodayString();
    const dailyDisease = getDailyDisease(today);
    setTargetDisease(dailyDisease);

    // Load today's game state if it exists
    const savedState = loadTodayGameState();
    if (savedState) {
      setGuesses(savedState.guesses || []);
      setGameWon(savedState.gameWon || false);
      setShowHint(savedState.showHint || false);
      setShowSecondHint(savedState.showSecondHint || false);

      // Check if the game is actually completed
      const completed = savedState.gameWon;
      setHasCompletedToday(completed);
    } else {
      // No saved state for today, start fresh
      setGuesses([]);
      setGameWon(false);
      setHasCompletedToday(false);
      setShowHint(false);
      setShowSecondHint(false);
    }
  };

  const saveStreak = (newStreak) => {
    setStreak(newStreak);
    // Save to localStorage if available in your environment
    if (typeof window !== 'undefined' && window.localStorage) {
      localStorage.setItem('diseaseGameStreak', newStreak.toString());
    }
  };

  const handleInputChange = (e) => {
    // Don't allow input if already completed today
    if (hasCompletedToday) return;

    const value = e.target.value;
    setCurrentGuess(value);

    if (value.length > 0) {
      const filtered = diseases.filter(d =>
        d.name.toLowerCase().includes(value.toLowerCase()) &&
        !guesses.some(g => g.name === d.name)
      );
      setSuggestions(filtered.slice(0, 5));
    } else {
      setSuggestions([]);
    }
  };

  const makeGuess = (diseaseName) => {
    // Don't allow guesses if already completed today
    if (hasCompletedToday) return;

    const disease = diseases.find(d => d.name === diseaseName);
    if (!disease || guesses.some(g => g.name === disease.name)) return;

    const newGuess = {
      ...disease,
      correct: disease.name === targetDisease.name
    };

    const newGuesses = [...guesses, newGuess];
    setGuesses(newGuesses);
    setCurrentGuess('');
    setSuggestions([]);

    if (disease.name === targetDisease.name) {
      setGameWon(true);
      setHasCompletedToday(true);
      const newStreak = streak + 1;
      saveStreak(newStreak);
      saveTodayGameState(newGuesses, true, showHint, showSecondHint);

      // Send Google Analytics event
      try {
        if (window.gtag) {
          window.gtag('event', 'game_complete', {
            guesses: newGuesses.length,
            disease_name: disease.name,
            used_hint_1: showHint,
            used_hint_2: showSecondHint,
            streak: newStreak
          });
        }
      } catch (error) {
        // Silently fail - don't impact user experience
      }
    } else {
      // Save progress but don't mark as completed
      saveTodayGameState(newGuesses, false, showHint, showSecondHint);
    }
  };

  const handleShowHint = () => {
    setShowHint(true);
    saveTodayGameState(guesses, gameWon, true, showSecondHint);
  };

  const handleShowSecondHint = () => {
    setShowSecondHint(true);
    saveTodayGameState(guesses, gameWon, showHint, true);
  };

  const handleShare = async () => {
    if (!gameWon || !targetDisease) return;

    // Generate squares for each guess
    const squares = guesses.map(guess => {
      const results = [];
      // Vaccine
      results.push(getComparisonClass(guess.vaccine, targetDisease.vaccine, false) === 'disease-game-exact-match' ? '🟩' :
                   getComparisonClass(guess.vaccine, targetDisease.vaccine, false) === 'disease-game-partial-match' ? '🟨' : '🟥');
      // Etiology
      results.push(getComparisonClass(guess.etiology, targetDisease.etiology, false) === 'disease-game-exact-match' ? '🟩' :
                   getComparisonClass(guess.etiology, targetDisease.etiology, false) === 'disease-game-partial-match' ? '🟨' : '🟥');
      // Min Incubation
      results.push(getIncubationComparisonClass(guess.incubationMin, targetDisease.incubationMin).class === 'disease-game-exact-match' ? '🟩' : '🟥');
      // Max Incubation
      results.push(getIncubationComparisonClass(guess.incubationMax, targetDisease.incubationMax).class === 'disease-game-exact-match' ? '🟩' : '🟥');
      // Treatment
      results.push(getComparisonClass(guess.treatment, targetDisease.treatment, false) === 'disease-game-exact-match' ? '🟩' :
                   getComparisonClass(guess.treatment, targetDisease.treatment, false) === 'disease-game-partial-match' ? '🟨' : '🟥');
      // Vector
      results.push(getComparisonClass(guess.vector, targetDisease.vector, false) === 'disease-game-exact-match' ? '🟩' :
                   getComparisonClass(guess.vector, targetDisease.vector, false) === 'disease-game-partial-match' ? '🟨' : '🟥');

      return results.join('');
    }).join('\n');

    const shareText = `Plagdle ${getTodayString()}\nSolved in ${guesses.length}/${guesses.length} 🦠\n\n${squares}\n\nhttps://plagdle.com`;

    try {
      await navigator.clipboard.writeText(shareText);
      setShareMessage('Copied to clipboard!');
      setTimeout(() => setShareMessage(''), 2000);
    } catch (err) {
      console.error('Failed to copy:', err);
      setShareMessage('Failed to copy');
      setTimeout(() => setShareMessage(''), 2000);
    }
  };

  const getComparisonClass = (guessValue, targetValue, isExactMatch = true) => {
    if (isExactMatch) {
      return guessValue === targetValue ? 'disease-game-exact-match' : 'disease-game-no-match';
    }
    // For partial matches (like etiology categories)
    const guessLower = guessValue.toLowerCase();
    const targetLower = targetValue.toLowerCase();
    if (guessLower === targetLower) return 'disease-game-exact-match';
    if (guessLower.includes(targetLower.split(' ')[0]) || targetLower.includes(guessLower.split(' ')[0])) {
      return 'disease-game-partial-match';
    }
    return 'disease-game-no-match';
  };

  // Function to get comparison class and direction for incubation periods
  const getIncubationComparisonClass = (guessValue, targetValue) => {
    if (guessValue === targetValue) {
      return { class: 'disease-game-exact-match', direction: null };
    }
    if (guessValue < targetValue) {
      return { class: 'disease-game-no-match', direction: 'up' };
    }
    return { class: 'disease-game-no-match', direction: 'down' };
  };

  return (
    <div className="disease-game-container">
      <div className="disease-game-wrapper">
        {/* Header */}
        <div className="disease-game-header">
          <div className="disease-game-title-wrapper">
            <Bug className="disease-game-bug-icon" />
            <h1 className="disease-game-title">Plagdle</h1>
            <Bug className="disease-game-bug-icon disease-game-bug-icon-flipped" />
          </div>
          <p className="disease-game-subtitle">Guess the infectious disease based on its characteristics!</p>



          {/* Stats */}
          <div className="disease-game-stats-container">
            <div className="disease-game-stat-box">
              <div className="disease-game-stat-number">{streak}</div>
              <div className="disease-game-stat-label">Streak</div>
            </div>
            <button
              onClick={handleShowHint}
              disabled={guesses.length < 3 || showHint}
              className={`disease-game-hint-button ${guesses.length < 3
                  ? 'disease-game-hint-button-disabled'
                  : showHint
                    ? 'disease-game-hint-button-active'
                    : ''
                }`}
            >
              <Lightbulb className="disease-game-hint-icon" />
              First Hint
            </button>
            <button
              onClick={handleShowSecondHint}
              disabled={guesses.length < 6 || showSecondHint}
              className={`disease-game-hint-button ${guesses.length < 6
                  ? 'disease-game-hint-button-disabled'
                  : showSecondHint
                    ? 'disease-game-hint-button-active'
                    : ''
                }`}
            >
              <Lightbulb className="disease-game-hint-icon" />
              Second Hint
            </button>
          </div>
        </div>

        {/* Game Area */}
        <div className="disease-game-area">
          {/* Input */}
          {!gameWon && !hasCompletedToday && (
            <div className="disease-game-input-wrapper">
              <input
                type="text"
                value={currentGuess}
                onChange={handleInputChange}
                onKeyPress={(e) => e.key === 'Enter' && currentGuess && makeGuess(currentGuess)}
                placeholder="Type a disease name..."
                className="disease-game-input"
              />

              {/* Suggestions dropdown */}
              {suggestions.length > 0 && (
                <div className="disease-game-suggestions" style={{ zIndex: 1000 }}>
                  {suggestions.map((disease, idx) => (
                    <div
                      key={idx}
                      onClick={() => makeGuess(disease.name)}
                      className="disease-game-suggestion-item"
                    >
                      {disease.name}
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}




          {/* Hint Display */}
          {(showHint || showSecondHint) && targetDisease && (
            <div className="disease-game-hint-display">
              {showHint && (
                <div className="disease-game-hint-content">
                  <div className="disease-game-hint-icon-wrapper">
                    <Lightbulb className="disease-game-hint-icon" />
                    <span className="disease-game-hint-number">1</span>
                  </div>
                  <span className="disease-game-hint-text">{targetDisease.smallHint}</span>
                </div>
              )}
              {showSecondHint && (
                <div className="disease-game-hint-content disease-game-second-hint">
                  <div className="disease-game-hint-icon-wrapper">
                    <Lightbulb className="disease-game-hint-icon" />
                    <span className="disease-game-hint-number">2</span>
                  </div>
                  <span className="disease-game-hint-text">{targetDisease.bigHint}</span>
                </div>
              )}
            </div>
          )}

          {/* Guesses Table */}
          {guesses.length > 0 && (
            <div className="disease-game-table-wrapper">
              <table className="disease-game-table">
                <thead>
                  <tr>
                    <th>Disease</th>
                    <th className="center">Vaccine</th>
                    <th className="center">Etiology</th>
                    <th className="center">Min Incubation</th>
                    <th className="center">Max Incubation</th>
                    <th className="center">Treatment</th>
                    <th className="center">Vector</th>
                  </tr>
                </thead>
                <tbody>
                  {guesses.map((guess, idx) => (
                    <tr key={idx}>
                      <td>
                        <div className="disease-game-disease-cell">
                          {guess.correct ? (
                            <Trophy className="disease-game-trophy-icon disease-game-button-icon" />
                          ) : (
                            <X className="disease-game-x-icon" />
                          )}
                          <span className="disease-game-disease-name">{guess.name}</span>
                        </div>
                      </td>
                      <td>
                        <div className={`disease-game-comparison-box ${getComparisonClass(guess.vaccine, targetDisease.vaccine, false)}`}>
                          {guess.vaccine}
                        </div>
                      </td>
                      <td>
                        <div className={`disease-game-comparison-box ${getComparisonClass(guess.etiology, targetDisease.etiology, false)}`}>
                          {guess.etiology}
                        </div>
                      </td>
                      <td>
                        <div className={`disease-game-comparison-box ${getIncubationComparisonClass(guess.incubationMin, targetDisease.incubationMin).class}`}>
                          <span className="disease-game-incubation-value">{guess.incubationMin}</span>
                          {getIncubationComparisonClass(guess.incubationMin, targetDisease.incubationMin).direction && (
                            <div className="disease-game-incubation-arrow">
                              {getIncubationComparisonClass(guess.incubationMin, targetDisease.incubationMin).direction === 'up' ? (
                                <ChevronUp className="disease-game-arrow-icon" />
                              ) : (
                                <ChevronDown className="disease-game-arrow-icon" />
                              )}
                            </div>
                          )}
                        </div>
                      </td>
                      <td>
                        <div className={`disease-game-comparison-box ${getIncubationComparisonClass(guess.incubationMax, targetDisease.incubationMax).class}`}>
                          <span className="disease-game-incubation-value">{guess.incubationMax}</span>
                          {getIncubationComparisonClass(guess.incubationMax, targetDisease.incubationMax).direction && (
                            <div className="disease-game-incubation-arrow">
                              {getIncubationComparisonClass(guess.incubationMax, targetDisease.incubationMax).direction === 'up' ? (
                                <ChevronUp className="disease-game-arrow-icon" />
                              ) : (
                                <ChevronDown className="disease-game-arrow-icon" />
                              )}
                            </div>
                          )}
                        </div>
                      </td>
                      <td>
                        <div className={`disease-game-comparison-box ${getComparisonClass(guess.treatment, targetDisease.treatment, false)}`}>
                          {guess.treatment}
                        </div>
                      </td>
                      <td>
                        <div className={`disease-game-comparison-box ${getComparisonClass(guess.vector, targetDisease.vector, false)}`}>
                          {guess.vector}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {/* Legend */}
          <div className="disease-game-legend">
            <div className="disease-game-legend-item">
              <div className="disease-game-legend-box disease-game-exact-match"></div>
              <span>Exact Match</span>
            </div>
            <div className="disease-game-legend-item">
              <div className="disease-game-legend-box disease-game-partial-match"></div>
              <span>Partial Match</span>
            </div>
            <div className="disease-game-legend-item">
              <div className="disease-game-legend-box disease-game-no-match"></div>
              <span>No Match</span>
            </div>
          </div>

          {/* Win Message */}
          {gameWon && targetDisease && (
            <div className="disease-game-win-message">
              <Trophy className="disease-game-icon disease-game-trophy-icon" />
              <h2 className="disease-game-win-title">Congratulations!</h2>
              <p className="disease-game-win-text">You found the disease in {guesses.length} {guesses.length === 1 ? 'guess' : 'guesses'}!</p>

              {/* Share Button */}
              <button onClick={handleShare} className="disease-game-share-button">
                <Share2 className="disease-game-share-icon" />
                Share Results
              </button>
              {shareMessage && (
                <p className="disease-game-share-message">{shareMessage}</p>
              )}

              {/* Trivia Section */}
              <div className="disease-game-trivia-section">
                <div className="disease-game-trivia-header">
                  <Lightbulb className="disease-game-trivia-icon" />
                  <h3 className="disease-game-trivia-title">Fun Fact!</h3>
                </div>
                <p className="disease-game-trivia-text">{targetDisease.trivia}</p>
              </div>

              <p className="disease-game-win-subtext">Come back tomorrow for a new daily puzzle!</p>
            </div>
          )}
        </div>

        {/* Instructions */}
        <div className="disease-game-instructions">
          <h3 className="disease-game-instructions-title">
            <AlertCircle className="disease-game-alert-icon" />
            How to Play
          </h3>
          <ul className="disease-game-instructions-list">
            <li className="disease-game-instruction-item">• One puzzle per day - same for all players</li>
            <li className="disease-game-instruction-item">• Guess the infectious disease based on its characteristics</li>
            <li className="disease-game-instruction-item">• Green = exact match, Yellow = partial match, Red = no match</li>
            <li className="disease-game-instruction-item">• Use the clues from each guess to narrow down the answer</li>
            <li className="disease-game-instruction-item">• Try to guess in as few attempts as possible!</li>
            <li className="disease-game-instruction-item">• Incubation periods are shown in days (min and max) with arrows indicating if you need to go higher ↑ or lower ↓</li>
            <li className="disease-game-instruction-item">• Hints unlock after 3 guesses (small hint) and 6 guesses (big hint) to help you narrow down the disease</li>
            <li className="disease-game-instruction-item">• <a href="https://docs.google.com/forms/d/1ooBNOeJ7SPnjHtUx8_Qe49uFMLkA88OFwsbwfrQrwjM/viewform?edit_requested=true" target="_blank" rel="noopener noreferrer" style={{color: '#93C5FD', textDecoration: 'underline'}}>Report errors or suggest improvements here</a></li>
          </ul>
        </div>

        {/* Footer */}
        <div className="disease-game-footer">
          <p className="disease-game-footer-text">
            Also try <a href="https://labbeaglegames.com/" target="_blank" rel="noopener noreferrer" className="disease-game-footer-link">Pharmageddon</a>!
          </p>
          <p className="disease-game-footer-text">
            Also try <a href="https://www.neurdle.com/" target="_blank" rel="noopener noreferrer" className="disease-game-footer-link">Neurdle</a>!
          </p>
        </div>
      </div>
    </div>
  );
}